// src/pages/healthcare/formsInTray/FormsInTrayPage.tsx

import { ENV } from '../../../config/runtimeEnv';
import React, { useState, useEffect, useCallback } from "react";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { Modal, Tag, Space, Button, Tooltip } from "antd";
import { ContrastTag } from "../../../components/shared";
import {
	ExclamationCircleOutlined, EyeOutlined, UserOutlined, BellOutlined,
	ReloadOutlined, CheckCircleOutlined, EditOutlined, TabletOutlined, QrcodeOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import type { ColumnsType } from "antd/es/table";

import { fetchInTrayForms, FormInTrayFilters, AssignedForm, updateAssignedForm, getAssignedForms } from "../../../api/formApi";
import { buildFormAssignedNotification } from "../../../utils/forms/formAssignedNotification";
import { isFormDone } from "../../../utils/forms/formStatus";
import { HandoverStartModal } from "../../../components/forms/HandoverExitGuard";
import FormShareLinkModal from "./FormShareLinkModal";
// The five canonical statuses live beside the transition matrix that says which moves between
// them are legal, so a filter and a target picker can never disagree about what a status is.
// This page only FILTERS by status — it never writes one — so it uses the whole list.
import { STATUS_OPTIONS } from "./formStatusTransitions";

import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import ReviewFormModal from "./ReviewFormModal";
import PractitionerFillFormModal from "./PractitionerFillFormModal";
import PatientFormFillModal from "./PatientFormFillModal";
import EditPatientModal from "../patient/PatientModal";

import { Patient } from "../../../types/healthcare/PatientTypes";
import { Company } from "../../../types/admin/CompanyTypes";

import { useNotifications } from "../../../context/NotificationContext";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { sendCommunication } from "../../../api/communications/communicationApi";
import type { SendCommunicationRequest } from "../../../types/communications/CommunicationTypes";
import { fetchClinicCompany } from "../../../api/admin/companyApi";

const ASSIGNEE_TYPE_OPTIONS = [
	{ label: "All", value: "" },
	{ label: "Patient Forms", value: "patient" },
	{ label: "Practitioner Forms", value: "practitioner" },
];

const statusColor: Record<string, string> = {
	Pending: "var(--mg-warning)",
	"In Progress": "var(--mg-info)",
	Completed: "var(--mg-success)",
	Reviewed: "var(--mg-success)",
	Cancelled: "var(--mg-danger)",
};

const FormsInTrayPage: React.FC = () => {
	const user = getSessionUser();
	const [forms, setForms]                       = useState<AssignedForm[]>([]);
	const [selectedForms, setSelectedForms]       = useState<AssignedForm[]>([]);
	const [isLoading, setIsLoading]               = useState(true);
	const [isRefreshing, setIsRefreshing]         = useState(false);

	const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
	const [currentForm, setCurrentForm]           = useState<AssignedForm | null>(null);
	const [fillFormId, setFillFormId]             = useState<number | null>(null);
	// Patient-typed on-behalf fill (F8). Holds the whole row, not an id: the modal names the
	// patient in its banner and the tray already has the record in hand.
	const [fillOnBehalfForm, setFillOnBehalfForm] = useState<AssignedForm | null>(null);
	// F10 hand-over. The tray listing carries no `access_token` (a cross-patient list must not
	// hand out capabilities), so the modal fetches it for this one patient on confirm.
	const [handoverForm, setHandoverForm] = useState<AssignedForm | null>(null);
	// F11 share link. The modal issues its own token via `link-issued`, so unlike the hand-over
	// above it needs nothing the tray listing withholds.
	const [shareForm, setShareForm] = useState<AssignedForm | null>(null);

	const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
	const [currentPatient, setCurrentPatient]     = useState<Patient | null>(null);
	const [pracCompany, setPracCompany]           = useState<Company | null>(null);
	const [isProcessing, setIsProcessing]         = useState(false);

	const { showError, showSuccess }              = useNotifications();

	const [pageNumber, setPageNumber]             = useState(1);
	const [totalForms, setTotalForms]             = useState(0);
	const [statusFilter, setStatusFilter]         = useState("");
	const [assigneeTypeFilter, setAssigneeTypeFilter] = useState<"" | "patient" | "practitioner">("");
	const [searchQuery, setSearchQuery]           = useState("");
	const [sortField, setSortField]               = useState<string | undefined>(undefined);
	const [sortOrder, setSortOrder]               = useState<"ascend" | "descend" | undefined>(undefined);

	const FORMS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	useEffect(() => {
		loadInTrayForms();
	}, [pageNumber, statusFilter, assigneeTypeFilter, searchQuery, sortField, sortOrder]);

	useEffect(() => {
		fetchClinicCompany().then(setPracCompany).catch(console.error);
	}, []);

	const loadInTrayForms = async () => {
		setIsLoading(true);
		try {
			const filters: FormInTrayFilters = {
				page_number: pageNumber,
				rows: FORMS_PER_PAGE,
				assignee_type: assigneeTypeFilter || undefined,
				filters: {
					...(statusFilter ? { status: [statusFilter] } : {}),
					...(searchQuery ? { patient_name: searchQuery } : {}),
				},
				sort_field: sortField,
				sort_order: sortOrder,
			};
			const { data, total } = await fetchInTrayForms(filters);
			setForms(data);
			setTotalForms(total);
		} catch {
			showError("Failed to load forms. Please try again later.");
		} finally {
			setIsLoading(false);
			setIsRefreshing(false);
		}
	};

	const handleReviewClick = useCallback((form: AssignedForm) => {
		setCurrentForm(form);
		setIsReviewModalOpen(true);
	}, []);

	const handleReviewModalClose = useCallback(() => {
		setIsReviewModalOpen(false);
		setCurrentForm(null);
		setSelectedForms([]);
	}, []);

	const handleOpenPatient = (patient: Patient) => {
		setCurrentPatient(patient);
		setIsPatientModalOpen(true);
	};

	const handleSendReminder = useCallback(
		(form: AssignedForm) => {
			if (isProcessing || form.assignee_type === 'practitioner') return;
			Modal.confirm({
				title: "Confirm Reminder",
				icon: <ExclamationCircleOutlined />,
				content: "Send a reminder for this form?",
				okText: "Send Reminder",
				okType: "primary",
				cancelText: "Cancel",
				onOk: async () => {
					setIsProcessing(true);
					try {
						// The tray listing deliberately does NOT carry `access_token` — it is a
						// cross-patient list and the token is a capability, so healthcare keeps
						// it hidden there. Fetch it for this one patient, only when a reminder
						// is actually being sent. A failed lookup yields a tokenless link, which
						// the grace period still honours — never a `t=undefined` link, which it
						// would not.
						let accessToken: string | undefined;
						try {
							const assignments = await getAssignedForms(form.patient_id);
							accessToken = assignments.find((a) => a.id === form.id)?.access_token;
						} catch {
							accessToken = undefined;
						}

						// One shared builder with the other three send sites (F11): see
						// `utils/forms/formAssignedNotification`.
						const payload: SendCommunicationRequest = buildFormAssignedNotification({
							assignedFormId: form.id,
							accessToken,
							patient: form.patient,
							companyId: pracCompany?.id || 0,
						});
						if (ENV.VITE_NOTIFICATIONS_ENABLED === "true") {
							sendCommunication(payload);
						}
						showSuccess("Reminder sent");
					} catch {
						showError("Failed to send reminder");
					} finally {
						setIsProcessing(false);
					}
				},
			});
		},
		[pracCompany, isProcessing],
	);

	const handleSaveForm = useCallback(async () => {
		await loadInTrayForms();
		handleReviewModalClose();
		showSuccess("Form updated successfully");
	}, [loadInTrayForms, handleReviewModalClose]);

	const handleRefresh = () => {
		setIsRefreshing(true);
		loadInTrayForms();
	};

	// "Done" is `Completed` OR `Reviewed`. Counting only `Completed` undercounted the
	// selection against the very rows the tray marks as finished, so the tag disagreed with
	// the per-row actions rendered right beneath it (:325 uses the same predicate).
	const completedCount = selectedForms.filter((f) => isFormDone(f.status)).length;

	const columns: ColumnsType<AssignedForm> = [
		{
			title: "Patient",
			key: "patient_name",
			fixed: "left",
			width: 180,
			render: (_, record) => {
				if (!record.patient) return "Unknown Patient";
				return (
					<div>
						<div style={{ fontWeight: 500 }}>{record.patient.first_name} {record.patient.last_name}</div>
						<div style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>ID: {record.patient.id}</div>
					</div>
				);
			},
		},
		{
			title: "Form Title",
			dataIndex: ["form", "title"],
			key: "form.title",
			width: 220,
			render: (text) => <div style={{ fontWeight: 500 }}>{text || "—"}</div>,
		},
		{
			title: "Assignee",
			key: "assignee",
			width: 160,
			render: (_, record) => {
				if (record.assignee_type === "practitioner") {
					const p = record.assigned_to_practitioner as any;
					return (
						<div>
							<Tag color="blue" style={{ marginBottom: 2 }}>Practitioner</Tag>
							<div style={{ fontSize: 12 }}>{p ? `${p.first_name} ${p.last_name}` : "—"}</div>
						</div>
					);
				}
				return (
					<div>
						<Tag color="default">Patient</Tag>
						<div style={{ fontSize: 12 }}>{record.patient ? `${record.patient.first_name} ${record.patient.last_name}` : "—"}</div>
					</div>
				);
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 130,
			align: "center",
			sorter: true,
			render: (status: string) => <ContrastTag background={statusColor[status] || undefined}>{status?.toUpperCase()}</ContrastTag>,
		},
		{
			title: "Assigned By",
			key: "practitioner",
			width: 160,
			render: (_, record) => {
				if (!record.practitioner) return "—";
				return `${(record.practitioner as any).first_name} ${(record.practitioner as any).last_name}`;
			},
		},
		{
			title: "Assigned Date",
			dataIndex: "created_at",
			key: "created_at",
			width: 140,
			sorter: true,
			render: (date: string) => {
				if (!date) return "—";
				try {
					const d = new Date(date);
					return <div><div>{format(d, "dd/MM/yyyy")}</div><div style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>{format(d, "HH:mm")}</div></div>;
				} catch { return date; }
			},
		},
		{
			title: "Updated",
			dataIndex: "updated_at",
			key: "updated_at",
			width: 140,
			sorter: true,
			render: (date: string) => {
				if (!date) return "—";
				try {
					const d = new Date(date);
					return <div><div>{format(d, "dd/MM/yyyy")}</div><div style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>{format(d, "HH:mm")}</div></div>;
				} catch { return date; }
			},
		},
		{
			title: "Next Appt",
			key: "next_appointment",
			width: 130,
			render: (_, record) => {
				try {
					const d = new Date(record.patient?.appointments[0]?.start_time);
					return <div><div>{format(d, "dd/MM/yyyy")}</div><div style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>{format(d, "HH:mm")}</div></div>;
				} catch { return "—"; }
			},
		},
		{
			title: "Response",
			key: "response",
			width: 100,
			align: "center",
			render: (_, record) => record.response
				? <ContrastTag background="var(--mg-success)">Submitted</ContrastTag>
				: <Tag>Pending</Tag>,
		},
		{
			title: "",
			key: "actions",
			// Five buttons on a pending patient row since F11
			// (fill / hand over / share / patient / remind).
			width: 210,
			align: "center",
			fixed: "right",
			render: (_, record) => {
				const isCompleted = isFormDone(record.status);
				const isPractitionerForm = record.assignee_type === "practitioner";
				return (
					<Space size={4}>
						{/* Both not-done branches get a "Fill Form" primary button; only the target
						    modal differs, because the two assignee types are served by two
						    different (and differently authorised) endpoint trios. A patient-typed
						    row used to fall through to Review, which is not a way to fill it. */}
						{isPractitionerForm && !isCompleted ? (
							<Tooltip title="Fill Form">
								<Button type="primary" size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); setFillFormId(record.id); }} />
							</Tooltip>
						) : !isPractitionerForm && !isCompleted ? (
							<Tooltip title="Fill Form on behalf of patient">
								<Button type="primary" size="small" icon={<EditOutlined />} data-testid="forms-tray-fill-on-behalf" onClick={(e) => { e.stopPropagation(); setFillOnBehalfForm(record); }} />
							</Tooltip>
						) : (
							<Tooltip title="Review">
								<Button type="primary" size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); handleReviewClick(record); }} />
							</Tooltip>
						)}
						{record.patient && (
							<Tooltip title="View Patient">
								<Button size="small" icon={<UserOutlined />} onClick={(e) => { e.stopPropagation(); handleOpenPatient(record.patient!); }} />
							</Tooltip>
						)}
						{/* F10 — hand this device to the patient (the sibling of "Fill on behalf"). */}
						{!isPractitionerForm && !isCompleted && (
							<Tooltip title="Hand to patient">
								<Button size="small" icon={<TabletOutlined />} data-testid="forms-tray-hand-to-patient" onClick={(e) => { e.stopPropagation(); setHandoverForm(record); }} />
							</Tooltip>
						)}
						{/* F11 — the third sibling: the patient's OWN device, so the clinical session
						    never leaves this one. Unlike hand-over, nothing here is a soft guard. */}
						{!isPractitionerForm && !isCompleted && (
							<Tooltip title="Open on patient's phone">
								<Button size="small" icon={<QrcodeOutlined />} data-testid="forms-tray-share-link" onClick={(e) => { e.stopPropagation(); setShareForm(record); }} />
							</Tooltip>
						)}
						{!isPractitionerForm && !isCompleted && (
							<Tooltip title="Send Reminder">
								<Button size="small" icon={<BellOutlined />} style={{ color: "var(--mg-warning)", borderColor: "var(--mg-warning)" }} onClick={(e) => { e.stopPropagation(); handleSendReminder(record); }} />
							</Tooltip>
						)}
					</Space>
				);
			},
		},
	];

	return (
		<Layout>
			{/* The e2e journey's screen anchor (F13). The tray is where the practitioner-facing
			    form actions live, so the Playwright spec needs one stable root to wait on before
			    it starts hunting for row actions. */}
			<PageContainer data-testid="forms-tray-root">
				<PageToolbar
					primaryAction={{
						label: "Review Form",
						icon: <EyeOutlined />,
						onClick: () => selectedForms.length === 1 && handleReviewClick(selectedForms[0]),
						disabled: selectedForms.length !== 1,
					}}
					extraActions={[
						{ label: "Refresh", icon: <ReloadOutlined />, onClick: handleRefresh },
					]}
					filters={[
						{
							type: "select",
							placeholder: "Filter by type",
							options: ASSIGNEE_TYPE_OPTIONS,
							value: assigneeTypeFilter,
							onChange: (v) => { setAssigneeTypeFilter(v as any); setPageNumber(1); },
							allowClear: true,
							width: 180,
						},
						{
							type: "select",
							placeholder: "Filter by status",
							options: STATUS_OPTIONS,
							value: statusFilter,
							onChange: (v) => { setStatusFilter(v); setPageNumber(1); },
							allowClear: true,
							width: 180,
						},
						{
							type: "search",
							placeholder: "Search patients...",
							value: searchQuery,
							onChange: (v) => { setSearchQuery(v); setPageNumber(1); },
							width: 260,
						},
						...(selectedForms.length > 0
							? [{
									type: "custom" as const,
									render: (
										<Space size={8}>
											<ContrastTag background="var(--mg-info)">{selectedForms.length} selected</ContrastTag>
											{completedCount > 0 && (
												<ContrastTag background="var(--mg-success)" icon={<CheckCircleOutlined />}>{completedCount} completed</ContrastTag>
											)}
										</Space>
									),
								}]
							: []),
					]}
					onReset={() => { setStatusFilter(""); setAssigneeTypeFilter(""); setSearchQuery(""); setPageNumber(1); }}
				/>
				<DataTable
					columns={columns}
					dataSource={forms}
					rowKey="id"
					loading={isLoading || isRefreshing}
					total={totalForms}
					currentPage={pageNumber}
					pageSize={FORMS_PER_PAGE}
					onPageChange={(page) => setPageNumber(page)}
					selectedRows={selectedForms}
					setSelectedRows={setSelectedForms}
					onRowClick={handleReviewClick}
					onSort={(field, order) => { setSortField(field); setSortOrder(order); }}
					scrollX={1600}
					fillHeight
					emptyTitle="No forms in tray"
					emptyDescription="No forms have been assigned yet"
				/>

				{isReviewModalOpen && currentForm && (
					<ReviewFormModal isOpen={isReviewModalOpen} onClose={handleReviewModalClose} onSave={handleSaveForm} assignedForm={currentForm} />
				)}

				{fillFormId !== null && (
					<PractitionerFillFormModal
						isOpen
						assignedFormId={fillFormId}
						patientName={forms.find((f) => f.id === fillFormId)?.patient ? `${forms.find((f) => f.id === fillFormId)!.patient!.first_name} ${forms.find((f) => f.id === fillFormId)!.patient!.last_name}` : undefined}
						onClose={() => { setFillFormId(null); loadInTrayForms(); }}
					/>
				)}

				{fillOnBehalfForm && (
					<PatientFormFillModal
						isOpen
						assignedForm={fillOnBehalfForm}
						onSave={loadInTrayForms}
						onClose={() => setFillOnBehalfForm(null)}
					/>
				)}

				{handoverForm && (
					<HandoverStartModal
						open
						assignedForm={handoverForm}
						patientName={handoverForm.patient ? `${handoverForm.patient.first_name ?? ""} ${handoverForm.patient.last_name ?? ""}`.trim() || undefined : undefined}
						onCancel={() => setHandoverForm(null)}
					/>
				)}

				{shareForm && (
					<FormShareLinkModal
						isOpen
						assignedForm={shareForm}
						patient={shareForm.patient}
						companyId={pracCompany?.id || 0}
						onClose={() => setShareForm(null)}
					/>
				)}

				{isPatientModalOpen && currentPatient && (
					<EditPatientModal isOpen={isPatientModalOpen} onClose={() => setIsPatientModalOpen(false)} patient={currentPatient} />
				)}
			</PageContainer>
		</Layout>
	);
};

export default FormsInTrayPage;
